/**
 * ThreeMusPanel — J5 3 Mu's diagnostic panel
 *
 * Renders three metric cards: Muda (waste), Muri (overload), Mura (unevenness).
 * Consumes the `useRorosThreeMus` hook and formats numbers via localeFormatters.
 *
 * Each card shows a count / value + a collapsible drill-down list.
 * Tooltips explain what each Mu means.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Zap, Activity } from 'lucide-react';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import type { ThreeMusResult } from '@/hooks/roros/three-mus';

interface ThreeMusPanelProps {
  data: ThreeMusResult;
  periodDays?: number;
}

interface MusCardProps {
  label: string;
  subtitle: string;
  tooltip: string;
  value: string;
  icon: React.ReactNode;
  colorClass: string;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
}

function MusCard({
  label,
  subtitle,
  tooltip,
  value,
  icon,
  colorClass,
  children,
  defaultExpanded = false,
}: MusCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasDetails = !!children;

  return (
    <div className={`rounded-lg border ${colorClass} bg-background`}>
      <div className="flex items-start justify-between p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">{icon}</div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-foreground">{label}</span>
              <SimpleTooltip content={tooltip}>
                <span
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground cursor-help"
                  tabIndex={0}
                  aria-label={t('More information')}
                >
                  ?
                </span>
              </SimpleTooltip>
            </div>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-foreground tabular-nums">{value}</span>
          {hasDetails && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              aria-label={expanded ? t('Collapse details') : t('Expand details')}
              className="flex-shrink-0"
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          )}
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="border-t border-border px-4 pb-4 pt-2">{children}</div>
      )}
    </div>
  );
}

export function ThreeMusPanel({ data, periodDays = 7 }: ThreeMusPanelProps) {
  const { muda, muri, mura } = data;

  const muraDisplay =
    mura.stdDev !== null
      ? `${(mura.stdDev * 100).toFixed(1)}%`
      : t('—');

  return (
    <div className="space-y-3" aria-label={t('3 Mu\'s diagnostic panel')} data-testid="three-mus-panel">
      <p className="text-xs text-muted-foreground">
        {t('Lean diagnostics for the last') + ' ' + periodDays + ' ' + t('days')}
      </p>

      {/* Muda — waste */}
      <MusCard
        label={t('Muda')}
        subtitle={t('Waste — cancelled items & QC failures')}
        tooltip={t('Muda = waste. Counts work items that were cancelled or failed quality confirmation in the period. Goal: reduce to 0.')}
        value={String(muda.count)}
        icon={<Trash2 className="h-5 w-5 text-error-500" aria-hidden />}
        colorClass="border-error-200"
        defaultExpanded={false}
      >
        {muda.items.length > 0 && (
          <ul className="space-y-1">
            {muda.items.slice(0, 10).map((item) => (
              <li key={item.id} className="flex items-center justify-between text-xs">
                <span className="truncate text-foreground max-w-[60%]">{item.title}</span>
                <span className="ml-2 flex-shrink-0 rounded-full bg-error-100 px-2 py-0.5 text-error-700 font-medium capitalize">
                  {item.status.replace(/_/g, ' ')}
                </span>
              </li>
            ))}
            {muda.items.length > 10 && (
              <li className="text-xs text-muted-foreground">
                {t('…and') + ' ' + (muda.items.length - 10) + ' ' + t('more')}
              </li>
            )}
          </ul>
        )}
      </MusCard>

      {/* Muri — overload */}
      <MusCard
        label={t('Muri')}
        subtitle={t('Overload — actors over WIP limit')}
        tooltip={t('Muri = overburden. Counts actors whose in-progress work items exceed their WIP limit. Fix: redistribute work or raise limits.')}
        value={String(muri.count)}
        icon={<Zap className="h-5 w-5 text-warning-500" aria-hidden />}
        colorClass="border-warning-200"
        defaultExpanded={false}
      >
        {muri.actors.length > 0 && (
          <ul className="space-y-1">
            {muri.actors.map((actor) => (
              <li key={actor.actorId} className="flex items-center justify-between text-xs">
                <span className="truncate text-foreground">{actor.actorName}</span>
                <span className="ml-2 flex-shrink-0 text-warning-700 font-medium">
                  {actor.inProgress}/{actor.limit}
                </span>
              </li>
            ))}
          </ul>
        )}
      </MusCard>

      {/* Mura — unevenness */}
      <MusCard
        label={t('Mura')}
        subtitle={t('Unevenness — FTT rate variation')}
        tooltip={t('Mura = unevenness. Measures the standard deviation of the weekly FTT (First Time Through) rate. Lower is better — aim for consistent delivery.')}
        value={muraDisplay}
        icon={<Activity className="h-5 w-5 text-primary-500" aria-hidden />}
        colorClass="border-primary-200"
        defaultExpanded={false}
      >
        {mura.samples > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('Based on') + ' ' + mura.samples + ' ' + t('weekly FTT samples')}
          </p>
        )}
      </MusCard>
    </div>
  );
}
